/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { UIRouter, UIView, useRouter } from '@uirouter/react';
import { Theme } from '@radix-ui/themes';
import {
  ExtJS,
  UnsavedChangesModal,
  PreviewUnsavedDialog,
  RouteLoadingFallback,
  AuthProvider,
  PermissionsProvider,
  StateProvider,
  ThemeProvider,
  ToastProvider,
  TooltipContainerProvider,
  useTheme,
  SessionExpiryModal,
  useSessionExpiry,
  GlobalHeader,
} from '@sonatype/nexus-ui-plugin';

import LeftNavigationMenuRadix from './components/LeftNavigationMenu';

import { getRouter } from './routerConfig/routerConfig';
import { ROUTE_NAMES } from './routerConfig/routeNames/routeNames';

import './App.scss';
import SystemNotices from './components/widgets/SystemStatusAlerts/SystemNotices';
import UpgradeModal from './components/pages/user/Welcome/UpgradeModal';
import ToastBridge from './components/shared/ToastBridge';
import { useRedirectOnLogout } from './hooks/useRedirectOnLogout';
import usePreventPushStateOnHash from './hooks/usePreventPushStateOnHash';
import UIStrings from './constants/UIStrings';

// Claim shell ownership at module load time (before any waitForExtJs callbacks fire).
// This prevents coreui-bundle from also rendering a shell on cloud deployments.
window.__nxAppRendered = true;

/**
 * Global session-expired modal (REST 401). Mounted for all routes so interceptors can always notify.
 */
function SessionExpiryHost() {
  const { isExpired, message, hideExpiryModal } = useSessionExpiry();
  return (
    <SessionExpiryModal
      isOpen={isExpired}
      onClose={hideExpiryModal}
      message={message}
    />
  );
}

// Wrapper to connect our ThemeProvider to Radix Theme (enables dark mode)
function AppWithRadixTheme() {
  const { activeTheme } = useTheme();

  return (
    <Theme appearance={activeTheme} accentColor="green" grayColor="slate" radius="medium">
      <TooltipContainerProvider>
        <ToastProvider>
          <ToastBridge>
            <SessionExpiryHost />
            <App />
          </ToastBridge>
        </ToastProvider>
      </TooltipContainerProvider>
    </Theme>
  );
}

export function App() {
  useRedirectOnLogout();
  usePreventPushStateOnHash();
  const { activeTheme } = useTheme();
  const router = useRouter();
  const currentStateName = router.globals.$current.name;
  const isLoginRoute = currentStateName === ROUTE_NAMES.LOGIN;

  // Render minimal layout for login route
  if (isLoginRoute) {
    return (
        <UIView />
    );
  }

  // Render standard layout for all other routes
  return (
      <>
        <SystemNotices/>

        <GlobalHeader
          routes={{
            searchGeneric: ROUTE_NAMES.BROWSE.SEARCH.GENERIC,
            previewSearchUnified: 'preview.browse.search.unified',
            previewSearchComponent: 'preview.browse.search.component',
            login: ROUTE_NAMES.LOGIN,
            userAccount: ROUTE_NAMES.USER.ACCOUNT,
            userNugetApiToken: ROUTE_NAMES.USER.NUGETAPITOKEN,
            userToken: ROUTE_NAMES.USER.USER_TOKEN,
            previewUserAccount: 'preview.user.account',
            previewUserNugetApiToken: 'preview.user.nugetapitoken',
            previewUserToken: 'preview.user.usertoken',
          }}
          accountMenuLabel={UIStrings.USER_ACCOUNT.MENU.text}
        />

        <LeftNavigationMenuRadix />

        <UpgradeModal/>

        <UnsavedChangesModal/>

        <PreviewUnsavedDialog/>

        <div className="nxrm-main-content">
          <Theme appearance={activeTheme} accentColor="blue" grayColor="slate" radius="medium">
            <Suspense fallback={<RouteLoadingFallback />}>
              <UIView/>
            </Suspense>
          </Theme>
        </div>
      </>);
}

// Build identifier - check in browser console: window.__nxBuild
window.__nxBuild = typeof __NX_BUILD_COMMIT__ !== 'undefined' ? __NX_BUILD_COMMIT__ : 'dev';

ExtJS.waitForExtJs(() => {
  const router = getRouter();
  const el = document.createElement('div');
  el.className = 'nx-page nxrm-page';
  document.body.appendChild(el);
  const root = createRoot(el);
  root.render(
    <ThemeProvider>
      <AuthProvider>
        <PermissionsProvider>
          <StateProvider>
            <UIRouter router={router}>
              <AppWithRadixTheme />
            </UIRouter>
          </StateProvider>
        </PermissionsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
});
